import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { SparklesIcon } from '@heroicons/react/24/outline'
import { requestAiCompletion } from '../../services/httpService'
import { useDarkMode } from '../../context/DarkModeContext'
import EmailSettingsModal from './EmailSettingsModal'
import TemplateBodyEditor from './TemplateBodyEditor'
import { emailSettingsStyles } from './emailSettingsStyles'
import { TemplatePreview } from './TemplatePreviewModal'
import { buildTemplatePrompt, parseGeneratedTemplate } from './templateAi'
import { cleanEmailHtml } from './cleanEmailHtml'

// Filled in by the DAT side when the email is sent.
const PLACEHOLDERS = ['origin', 'destination', 'pick_date', 'equipment_type', 'trip_miles', 'deadhead', 'reference_id']
const MAX_CC = 3

const hasText = html => Boolean(html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim())

const TemplateModal = ({ template, onSave, onClose }) => {
	const { isDarkMode } = useDarkMode()
	const isLight = !isDarkMode
	const styles = emailSettingsStyles(isLight)
	const isCreating = !template
	const [name, setName] = useState(template?.name || '')
	const [subject, setSubject] = useState(template?.subject || '')
	const [cc, setCc] = useState((template?.cc || []).join(', '))
	const [body, setBody] = useState(template?.body || '')
	const hasChanges =
		name !== (template?.name || '') ||
		subject !== (template?.subject || '') ||
		cc !== (template?.cc || []).join(', ') ||
		body !== (template?.body || '')
	const requestClose = () => {
		if (!hasChanges || window.confirm('Discard your changes?')) onClose()
	}
	const [saving, setSaving] = useState(false)
	const [generating, setGenerating] = useState(false)
	const [mode, setMode] = useState('edit')
	const [error, setError] = useState(null)
	const subjectRef = useRef(null)
	const bodyEditorRef = useRef(null)
	const lastFocusedField = useRef('body')

	const clearError = () => setError(null)

	const insertPlaceholder = placeholder => {
		const text = `{{${placeholder}}}`
		clearError()
		if (lastFocusedField.current === 'subject') {
			const input = subjectRef.current
			const start = input.selectionStart ?? subject.length
			const end = input.selectionEnd ?? subject.length
			setSubject(subject.slice(0, start) + text + subject.slice(end))
			requestAnimationFrame(() => {
				input.focus()
				input.setSelectionRange(start + text.length, start + text.length)
			})
			return
		}
		bodyEditorRef.current.insertText(text)
	}

	const handleGenerate = async () => {
		setGenerating(true)
		clearError()
		try {
			const completion = await requestAiCompletion(buildTemplatePrompt({ name, subject, body }))
			const generated = parseGeneratedTemplate(completion)
			if (!name.trim() && generated.name) setName(generated.name)
			setSubject(generated.subject)
			bodyEditorRef.current.setHtml(cleanEmailHtml(generated.body))
		} catch (err) {
			setError({ message: err.response?.data?.error || "Couldn't generate a template. Try again." })
		} finally {
			setGenerating(false)
		}
	}

	const ccList = cc.split(',').map(address => address.trim()).filter(Boolean)

	const handleSave = async () => {
		if (!name.trim() || !subject.trim() || !hasText(body)) {
			setError({ message: 'Fill in the name, subject and body.' })
			return
		}
		if (ccList.length > MAX_CC) {
			setError({ message: `CC is limited to ${MAX_CC} addresses.` })
			return
		}

		setSaving(true)
		try {
			await onSave({ name: name.trim(), subject: subject.trim(), body, cc: ccList, signatureId: template?.signatureId || null })
			onClose()
		} catch (err) {
			setError(err)
		} finally {
			setSaving(false)
		}
	}

	return (
		<EmailSettingsModal
			title={isCreating ? 'New template' : 'Edit template'}
			onClose={requestClose}
			footer={
				<>
					<button
						type="button"
						onClick={handleGenerate}
						disabled={generating || saving}
						className={`${styles.secondaryButton} w-full sm:w-auto sm:mr-auto py-2 inline-flex items-center justify-center gap-1.5`}
					>
						<SparklesIcon className="w-4 h-4" />
						{generating ? 'Generating…' : 'Generate with AI'}
					</button>
					<button type="button" onClick={requestClose} className={`${styles.secondaryButton} w-full sm:w-auto py-2`}>
						Cancel
					</button>
					<button type="button" onClick={handleSave} disabled={saving} className={`${styles.primaryButton} w-full sm:w-auto`}>
						{saving ? 'Saving…' : isCreating ? 'Create template' : 'Save template'}
					</button>
				</>
			}
		>
			{error && (
				<div role="alert" className={`${styles.errorBox} mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2`}>
					<span>{error.message}</span>
					{error.needsUpgrade && (
						<Link to="/settings/subscription" className="font-semibold underline whitespace-nowrap">
							See plans
						</Link>
					)}
				</div>
			)}
			<div
				role="tablist"
				className={`inline-flex gap-0.5 p-0.5 mb-4 rounded-md ${isLight ? 'bg-gray-100' : 'bg-[#0f1923]'}`}
			>
				{['edit', 'preview'].map(tab => (
					<button
						key={tab}
						type="button"
						role="tab"
						aria-selected={mode === tab}
						onClick={() => setMode(tab)}
						className={`px-3 py-1 rounded text-sm font-medium capitalize ${
							mode === tab
								? isLight ? 'bg-white text-gray-900 shadow-sm' : 'bg-[#263340] text-white'
								: isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'
						}`}
					>
						{tab}
					</button>
				))}
			</div>
			{mode === 'preview' && (
				<div>
					<p className={`text-xs mb-2 ${styles.muted}`}>With a sample load</p>
					<TemplatePreview subject={subject} body={body} cc={ccList} />
				</div>
			)}
			{/* Hidden, not unmounted, so the body editor keeps its content while previewing. */}
			<div className={`space-y-4 ${mode === 'preview' ? 'hidden' : ''}`}>
				<div>
					<label className={styles.label} htmlFor="template-name">Name</label>
					<input
						id="template-name"
						value={name}
						placeholder="Rate check"
						onChange={event => { setName(event.target.value); clearError() }}
						className={styles.input}
					/>
				</div>
				<div>
					<label className={styles.label} htmlFor="template-subject">Subject</label>
					<input
						id="template-subject"
						ref={subjectRef}
						value={subject}
						placeholder="Load from {{origin}} to {{destination}}"
						onFocus={() => { lastFocusedField.current = 'subject' }}
						onChange={event => { setSubject(event.target.value); clearError() }}
						className={styles.input}
					/>
				</div>
				<div>
					<label className={styles.label} htmlFor="template-cc">CC (up to {MAX_CC}, separated by commas)</label>
					<input
						id="template-cc"
						value={cc}
						placeholder="dispatch@yourcompany.com"
						onChange={event => { setCc(event.target.value); clearError() }}
						className={styles.input}
					/>
				</div>
				<div>
					<p className={styles.label}>Body</p>
					<TemplateBodyEditor
						ref={bodyEditorRef}
						initialHtml={template?.body}
						isLight={isLight}
						onFocus={() => { lastFocusedField.current = 'body' }}
						onChange={html => { setBody(html); clearError() }}
					/>
				</div>
				<div>
					<p className={styles.label}>Insert a placeholder</p>
					<div className="flex flex-wrap gap-2">
						{PLACEHOLDERS.map(placeholder => (
							<button
								key={placeholder}
								type="button"
								onMouseDown={event => event.preventDefault()}
								onClick={() => insertPlaceholder(placeholder)}
								className={styles.chip}
							>
								{`{{${placeholder}}}`}
							</button>
						))}
					</div>
				</div>
			</div>
		</EmailSettingsModal>
	)
}

export default TemplateModal
